import type { CollectionPreferencesProps } from '@risk-smart/themed-cloudscape-components/collection-preferences';
import type { TableProps } from '@risk-smart/themed-cloudscape-components/table';
import Table from '@risksmart-app/components/src/table';
import type {
  GetFormCustomisationQuery,
  GetReportingDataQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { type FC, type ReactNode } from 'react';
import { useModulesStore } from 'src/context/moduleContext';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type { TypedCustomDatasource } from '../types';
import { useCustomDatasourceHelpers } from '../useCustomDatasourceHelpers';
import { reconcileContentDisplay } from './contentDisplay';
import CustomDatasourceCollectionPreferences from './CustomDatasourceCollectionPreferences';
import { CustomDatasourceModel } from './customDatasourceModel';
import { displayTypes } from './display-types';
import Pager from './Pager';
import type { CustomAttributeSchemaLookup } from './types';

export type Props = {
  variant?: TableProps.Variant;
  customDatasource: Pick<TypedCustomDatasource, 'Datasources' | 'Fields'>;
  items: GetReportingDataQuery['reportingData'];
  onPageChangeClick?: (e: { requestedPageIndex: number }) => void;
  currentPageIndex: number;
  pageSize: number;
  loading: boolean;
  customAttributeSchemaLookup: CustomAttributeSchemaLookup;
  formFieldConfigurations:
    | GetFormCustomisationQuery['form_field_configuration']
    | null;
  filter?: ReactNode;
  /**
   * Set to false to disable ability to hide table columns in preferences
   */
  columnsAlwaysVisible: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  preferences?: CollectionPreferencesProps.Preferences<any>;
  onSetPreferences: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    preferences: CollectionPreferencesProps.Preferences<any>
  ) => void;
};

const CustomDatasourceTable: FC<Props> = ({
  customDatasource,
  columnsAlwaysVisible,
  items,
  onPageChangeClick,
  currentPageIndex,
  pageSize,
  loading,
  customAttributeSchemaLookup,
  formFieldConfigurations,
  variant,
  filter,
  preferences,
  onSetPreferences,
}) => {
  const formSectionsEnabled = useIsFeatureFlagEnabled('form_sections');
  const modules = useModulesStore((s) => s.modules);
  const helpers = useCustomDatasourceHelpers();
  const customDatasourceModel = CustomDatasourceModel(
    customDatasource,
    customAttributeSchemaLookup,
    formFieldConfigurations,
    formSectionsEnabled,
    modules
  );

  // CUS-207: fields referencing a custom attribute deleted from the schema are
  // kept in `fields` as positional placeholders so the row index stays aligned
  // with the NULL cell the backend still emits for them. Don't render them as
  // (empty) columns — drop them from the table while keeping the surviving
  // fields' original index for the positional cell lookup. A notice on the
  // detail page tells the user the field no longer exists.
  const deletedFieldValues = new Set(
    customDatasourceModel.deletedFields.map((field) => field.value)
  );
  const visibleFields = customDatasourceModel.fields
    .map((field, index) => ({ field, index }))
    .filter(({ field }) => !deletedFieldValues.has(field.value));

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columnDefinitions: readonly TableProps.ColumnDefinition<any>[] =
    visibleFields.map(({ field, index }) => {
      return {
        id: field.value,
        header: field?.label ?? field?.defaultLabel,
        cell: (item) => {
          const fieldDef = field;
          if (!fieldDef) {
            throw new Error('Missing field def');
          }

          return displayTypes[fieldDef.displayType].cell({
            fieldDef,
            fieldData: item[index],
            helpers,
          });
        },
      };
    }) ?? [];

  // Reconcile the (possibly stale) column-preferences state against the current field
  // selection so that newly-enabled fields surface as visible columns and toggle on in
  // the preferences modal rather than being silently dropped (RSP-6056).
  const reconciledContentDisplay = reconcileContentDisplay(
    preferences?.contentDisplay,
    visibleFields.map(({ field }) => field.value)
  );
  const reconciledPreferences = preferences
    ? { ...preferences, contentDisplay: reconciledContentDisplay }
    : preferences;

  return (
    <Table
      preferences={
        <CustomDatasourceCollectionPreferences
          columnsAlwaysVisible={columnsAlwaysVisible}
          fields={visibleFields.map(({ field }) => field)}
          preferences={reconciledPreferences}
          onConfirm={(e) => {
            onSetPreferences(e.detail);
          }}
        />
      }
      columnDisplay={reconciledContentDisplay}
      wrapLines={reconciledPreferences?.wrapLines}
      stripedRows={reconciledPreferences?.stripedRows}
      contentDensity={reconciledPreferences?.contentDensity}
      stickyColumns={reconciledPreferences?.stickyColumns}
      filter={filter}
      loading={loading}
      variant={variant}
      pagination={
        onPageChangeClick && (
          <Pager
            loading={loading}
            currentPageIndex={currentPageIndex}
            pageSize={pageSize}
            currentPageSize={items?.length ?? 0}
            onPageChangeClick={onPageChangeClick}
          />
        )
      }
      items={items ?? []}
      columnDefinitions={columnDefinitions}
      empty={'No records found'}
    />
  );
};

export default CustomDatasourceTable;
